import { Icon } from "@chakra-ui/react";
import { MdBarChart } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoGameCount, getDemoGrowth, isDemoOrganization } from "../demoAnalyticsData";
import { AnalyticsFilterParams } from "../hooks/analyticsFilterParams";
import { useGetGameCount } from "../hooks/useGetGameCount";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
    filters?: AnalyticsFilterParams;
};
export default function GameCountCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel, filters }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * Every `analytics/*` route carries `@UseGuards(AdminAuthGuard)` — read from
     * `api/src/analytics/analytics.controller.ts` this run, not inherited — so a
     * stale dashboard token or an operator who is no longer an admin of this org
     * is REFUSED, with no outage at all, on top of the ordinary 5xx/network
     * paths. All of them leave `data === undefined`, which this card used to
     * render as a real number.
     */
    const { data: totalGameCount, isInitialLoading: isTotalPending, isError: totalError } = useGetGameCount(selectedLocation?.id, undefined, undefined, filters);
    const { data, isInitialLoading: isPeriodPending, isError: periodError } = useGetGameCount(selectedLocation?.id, startDate, endDate, filters);
    // Comparison read: feeds `computeGrowth` only, which returns undefined when
    // either side is undefined, so a failed read removes the growth chip rather
    // than mis-stating it. Allowlisted in `analytics-read-error-gate`.
    const { data: previousData } = useGetGameCount(
        selectedLocation?.id,
        previousStartDate ?? undefined,
        previousEndDate ?? undefined,
        filters,
    );

    const orgId = selectedOrganization?.id;
    const totalUnreadable = isUnreadable(totalError, totalGameCount);
    const periodUnreadable = isUnreadable(periodError, data);
    const demoTotal = isDemo ? getDemoGameCount(undefined, undefined, orgId) : 0;
    const demoPeriod = isDemo ? getDemoGameCount(startDate, endDate, orgId) : 0;
    // The last arm was a literal `'0'`, reached whenever the read had no data
    // and was not loading — i.e. with no location selected, where the query is
    // DISABLED and answers nothing forever (R100-1). A genuinely quiet period
    // still prints `0` through the `Boolean(...)` arm: the API answers an
    // object, `{ value: 0 }`.
    const totalValue = isDemo ? demoTotal : totalUnreadable ? UNKNOWN_STAT_VALUE : Boolean(totalGameCount) ? totalGameCount?.value : statPlaceholder(isTotalPending);
    const partialValue = isDemo ? demoPeriod : periodUnreadable ? UNKNOWN_STAT_VALUE : Boolean(data) ? data?.value : statPlaceholder(isPeriodPending);
    const growth = isDemo
        ? getDemoGrowth(demoPeriod, previousStartDate, previousEndDate, getDemoGameCount, orgId)
        : previousStartDate ? computeGrowth(data?.value, previousData?.value) : undefined;

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />}
                />
            }
            name={translate('analytics.games')}
            value={`${partialValue} / ${totalValue}`}
            isError={!isDemo && (periodUnreadable || totalUnreadable)}
            growth={growth}
            growthLabel={growthLabel}
        />)
}
